import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import EnhancedTripRow from "../../../components/agent/trips/EnhancedTripRow";
import CreateTripModal from "../../../components/agent/trips/CreateTripModal";
import { EmptyTrips, EmptySearch } from "../../../components/agent/UX/EmptyState";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
import usePixieDustNavigation from "../../../hooks/usePixieDustNavigation";
import {
  Plus,
  Search,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Castle,
  MapPin,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";
import { useUser } from "../../../context/UserContext";

export default function DisneyWorld() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const _navigate = useNavigate();
  
  // Pixie dust navigation hook
  const {
    navigateToUnifiedItinerary
  } = usePixieDustNavigation();

  const location = useLocation();

  const { _user } = useUser();

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        // Fetch only Disney World trips using destination parameter
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/trips?destination=disney_world`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error("Failed to fetch trips");
        const data = await res.json();
        
        setTrips(data);
      } catch (err) {
        console.error("Error loading Disney World trips:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, []);

  const handleTripCreated = (newTrip) => {
    setTrips([newTrip, ...trips]);
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/trips/${selectedId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to delete trip");

      setTrips(trips.filter((trip) => trip.tripid !== selectedId));
      setShowDeleteModal(false);
      setSelectedId(null);
      toast.success("Trip deleted successfully!");
    } catch (err) {
      console.error("Error deleting trip:", err);
      toast.error("Failed to delete trip");
    }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link. Please try manually.");
    }
  };

  const filteredTrips = trips.filter((trip) => {
    const tripName = trip.tripname?.toLowerCase() || "";
    return tripName.includes(searchQuery.toLowerCase());
  });

  // Calculate Disney World specific stats
  const totalTrips = trips.length;
  const completedTrips = trips.filter(
    (trip) => trip.status === "completed"
  ).length;
  const pendingTrips = trips.filter((trip) => trip.status === "pending").length;
  const _recentTrips = trips.slice(0, 5);

  return (
    <>
      <div className="animate-fade-in" style={{ opacity: 1 }}>
            {/* Header */}
            <div className="mb-4">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center sm:mb-3">
                <div>
                  <div className="flex gap-3 items-center mb-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Castle className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-black sm:text-2xl">
                        Disney World Trips
                      </h1>
                      <p className="text-sm text-gray-600">
                        Manage your Disney World trips and create magical experiences for your clients
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 sm:px-4 sm:py-2"
                >
                  <Plus className="mr-2 w-4 h-4" />
                  <span className="hidden sm:inline">Create Disney Trip</span>
                  <span className="sm:hidden">New Trip</span>
                </button>
              </div>

              {/* Disney World Stats Cards */}
              <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4 sm:gap-4 animate-fade-in-up">
                <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs font-medium text-gray-600 sm:text-sm">
                        Disney Trips
                      </p>
                      <p className="text-xl font-bold text-black sm:text-2xl">
                        {totalTrips}
                      </p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg sm:p-3">
                      <Castle className="w-5 h-5 text-blue-600 sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs font-medium text-gray-600 sm:text-sm">
                        Completed
                      </p>
                      <p className="text-xl font-bold text-black sm:text-2xl">
                        {completedTrips}
                      </p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg sm:p-3">
                      <CheckCircle className="w-5 h-5 text-green-600 sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs font-medium text-gray-600 sm:text-sm">
                        Planning
                      </p>
                      <p className="text-xl font-bold text-black sm:text-2xl">
                        {pendingTrips}
                      </p>
                    </div>
                    <div className="p-2 bg-yellow-100 rounded-lg sm:p-3">
                      <Clock className="w-5 h-5 text-yellow-600 sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs font-medium text-gray-600 sm:text-sm">
                        Magic Rate
                      </p>
                      <p className="text-xl font-bold text-black sm:text-2xl">
                        {totalTrips > 0
                          ? Math.round((completedTrips / totalTrips) * 100)
                          : 0}
                        %
                      </p>
                    </div>
                    <div className="p-2 bg-purple-100 rounded-lg sm:p-3">
                      <TrendingUp className="w-5 h-5 text-purple-600 sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Disney World Info Banner */}
              <div className="p-4 mb-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                <div className="flex gap-3 items-start">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="mb-1 text-sm font-semibold text-blue-900">
                      Disney World Destinations
                    </h3>
                    <p className="text-xs text-blue-700">
                      Magic Kingdom • EPCOT • Hollywood Studios • Animal Kingdom • Disney Springs • Water Parks
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between animate-fade-in-up">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search Disney World trips..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-2 pr-3 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="text-xs text-gray-600 sm:text-sm">
                {filteredTrips.length} of {trips.length} Disney World trips
              </div>
            </div>

            {/* Disney World Trips Section */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-3 border-b border-gray-200 sm:p-4">
                <div>
                  <h2 className="text-base font-semibold text-black sm:text-lg">
                    Your Disney World Trips
                  </h2>
                  <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                    Manage your Disney World trip itineraries and create magical experiences
                  </p>
                </div>
              </div>

              <div className="p-3 sm:p-4">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner
                      size="lg"
                      text="Loading your Disney World trips..."
                    />
                  </div>
                ) : filteredTrips.length === 0 ? (
                  searchQuery ? (
                    <EmptySearch searchTerm={searchQuery} />
                  ) : (
                    <div className="py-12 text-center">
                      <div className="p-4 mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full">
                        <Castle className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        No Disney World Trips Yet
                      </h3>
                      <p className="mb-6 text-gray-600">
                        Start creating magical Disney World experiences for your clients
                      </p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                      >
                        <Plus className="mr-2 w-4 h-4" />
                        Create Your First Disney Trip
                      </button>
                    </div>
                  )
                ) : (
                  <div className="space-y-2 animate-fade-in-up">
                    {filteredTrips.map((trip) => (
                      <EnhancedTripRow
                        key={trip.tripid || trip.id}
                        trip={trip}
                        client={{
                          fullname: trip.fullname,
                          email: trip.clientemail,
                          phone: trip.clientphone,
                          clientid: trip.clientid
                        }}
                        onClick={(tripId) => {
                          if (trip.status === "completed") {
                            navigateToUnifiedItinerary(tripId || trip.tripid);
                          }
                        }}
                        onUnifiedNavigation={navigateToUnifiedItinerary}
                        onDelete={() => {
                          setSelectedId(trip.tripid);
                          setShowDeleteModal(true);
                        }}
                        onCopyLink={handleCopyLink}
                        hasCRMAccess={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-lg">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-black">
                Delete Disney World Trip
              </h3>
            </div>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete this Disney World trip? This action cannot be
              undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTripCreated={handleTripCreated}
          defaultDestination="disney_world"
        />
      )}

      {/* Floating Action Button for Mobile */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="fixed right-6 bottom-6 z-40 p-4 text-white bg-blue-600 rounded-full shadow-lg transition-all duration-200 hover:bg-blue-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-gray-400 md:hidden"
      >
        <Plus className="w-6" />
      </button>
    </>
  );
}
